"use client";

import { useState, useEffect } from "react";
import { usePathname } from "next/navigation";
import { Button } from "@/components/ui/button";
import { LeadForm } from "@/components/ui/lead-form";

// navigation now depends on current pathname (bespoke "/" vs classic home "/home")

export function HeaderCTA({
  isTransparent = false,
  onOpenForm,
}: {
  isTransparent?: boolean;
  onOpenForm?: () => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("home");
  const [isMounted, setIsMounted] = useState(false);
  const pathname = usePathname();

  // Build navigation based on current route
  const navigation =
    pathname === "/"
      ? [
          { name: "Home", href: "/", isHome: true },
          { name: "Features", href: "#features", isHome: false },
          {
            name: "Our Previous Work",
            href: "#our-previous-work",
            isHome: false,
          },
          { name: "Pricing", href: "#pricing", isHome: false },
          { name: "About", href: "/about", isHome: false },
          { name: "Contact", href: "/contact", isHome: false },
        ]
      : [
          { name: "Home", href: "/", isHome: true },
          { name: "Features", href: "/home#features", isHome: false },
          { name: "How It Works", href: "/home#how-it-works", isHome: false },
          { name: "About", href: "/about", isHome: false },
          { name: "Contact", href: "/contact", isHome: false },
        ];

  // Track if component is mounted to avoid hydration mismatch
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Track active section based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      const sections =
        pathname === "/"
          ? ["features", "our-previous-work", "pricing"]
          : ["features", "how-it-works"]; // pricing hidden
      const scrollPosition = window.scrollY + 100; // Offset for header

      // Check if we're at the top (home section)
      if (scrollPosition < 100) {
        setActiveSection("home");
        return;
      }

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    // Add scroll listener on bespoke home "/" and classic home "/home"
    if (isMounted && (pathname === "/" || pathname === "/home")) {
      window.addEventListener("scroll", handleScroll);
      return () => window.removeEventListener("scroll", handleScroll);
    }
  }, [isMounted, pathname]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (
        isOpen &&
        !target.closest("#mobile-menu") &&
        !target.closest('button[aria-controls="mobile-menu"]')
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  // Close mobile menu on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    }
  }, [isOpen]);

  const handleNavClick = (href: string, isHome: boolean) => {
    if (isHome) {
      window.location.href = href;
    } else if (href.startsWith("#")) {
      // bespoke section anchors on "/"
      if (pathname !== "/") {
        window.location.href = `/${href}`;
        setIsOpen(false);
        return;
      }
      const element = document.querySelector(href);
      if (element) {
        const headerHeight = 80;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition =
          elementPosition + window.pageYOffset - headerHeight;
        if ("scrollBehavior" in document.documentElement.style) {
          window.scrollTo({ top: offsetPosition, behavior: "smooth" });
        } else {
          window.scrollTo(0, offsetPosition);
        }
      }
    } else {
      // Handle page navigation
      window.location.href = href;
    }
    setIsOpen(false);
  };

  return (
    <>
      {/* Desktop Navigation - Center */}
      <div className="hidden xl:flex xl:items-center xl:justify-center xl:flex-1">
        <ul className="flex items-center space-x-8" role="menubar">
          {navigation.map((item) => {
            const isActive = isMounted
              ? item.isHome
                ? pathname === "/"
                : pathname === item.href
              : false;

            return (
              <li key={item.name} role="none">
                <button
                  onClick={() => handleNavClick(item.href, item.isHome)}
                  className={`text-sm font-medium transition-all duration-300 ${
                    isActive
                      ? item.name === "Bespoke"
                        ? "text-amber-400 font-semibold drop-shadow-lg drop-shadow-amber-400/50"
                        : isTransparent
                        ? "text-amber-400 font-semibold drop-shadow-lg drop-shadow-amber-400/50"
                        : "text-primary font-semibold drop-shadow-lg drop-shadow-primary/50"
                      : item.name === "Bespoke"
                      ? "text-amber-400 hover:text-amber-200 hover:drop-shadow-lg hover:drop-shadow-amber-400/70 hover:scale-105"
                      : isTransparent
                      ? item.name === "Home"
                        ? "text-white hover:text-amber-300 hover:drop-shadow-lg hover:drop-shadow-amber-300/50 hover:scale-105"
                        : item.name === "About"
                        ? "text-white hover:text-amber-300 hover:drop-shadow-lg hover:drop-shadow-amber-300/50 hover:scale-105"
                        : item.name === "Contact"
                        ? "text-white hover:text-amber-300 hover:drop-shadow-lg hover:drop-shadow-amber-300/50 hover:scale-105"
                        : "text-white hover:text-amber-300 hover:drop-shadow-lg hover:drop-shadow-amber-300/50 hover:scale-105"
                      : "text-muted-foreground hover:text-primary hover:drop-shadow-lg hover:drop-shadow-primary/50 hover:scale-105"
                  }`}
                  role="menuitem"
                  aria-label={`Navigate to ${item.name}`}
                >
                  {item.name}
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Join Waitlist Button - Right side */}
      <div className="hidden xl:flex xl:items-center">
        <Button
          onClick={() => {
            if (onOpenForm) {
              onOpenForm();
            } else {
              setIsFormOpen(true);
            }
          }}
          className={`text-sm px-4 transition-all duration-300 ${
            isTransparent
              ? "bg-white/20 hover:bg-white/30 text-white border border-white/30 backdrop-blur-sm"
              : "bg-foreground text-background hover:bg-foreground/70"
          }`}
          aria-label="Join waitlist for PhotoZoot"
        >
          Join Waitlist
        </Button>
      </div>

      {/* Mobile/Tablet Menu Button - Visible on mobile and tablet */}
      <div className="xl:hidden">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`p-2 rounded-md transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
            isTransparent
              ? "text-white hover:bg-white/20 focus:ring-white/50"
              : "text-foreground hover:bg-muted/50 focus:ring-primary"
          }`}
          aria-label={isOpen ? "Close mobile menu" : "Open mobile menu"}
          aria-expanded={isOpen}
          aria-controls="mobile-menu"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            {isOpen ? (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            ) : (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            )}
          </svg>
        </button>
      </div>

      {/* Mobile/Tablet Menu - Positioned absolutely below the header */}
      <div
        id="mobile-menu"
        className={`absolute top-full left-0 right-0 xl:hidden border-t shadow-lg z-50 transition-all duration-300 ease-in-out ${
          isTransparent
            ? "border-white/20 bg-black/80 backdrop-blur-sm"
            : "border-border/40 bg-background/95 backdrop-blur-sm"
        } ${
          isOpen
            ? "opacity-100 translate-y-0"
            : "opacity-0 -translate-y-2 pointer-events-none"
        }`}
        role="navigation"
        aria-label="Mobile navigation"
      >
        <div className="py-4 px-4">
          <ul className="space-y-2" role="menubar">
            {navigation.map((item) => {
              const isActive = isMounted
                ? item.isHome
                  ? pathname === "/"
                  : pathname === item.href
                : false;

              return (
                <li key={item.name} role="none">
                  <button
                    onClick={() => handleNavClick(item.href, item.isHome)}
                    className={`block w-full text-left px-3 py-2 text-base font-medium transition-all duration-300 rounded-md ${
                      isActive
                        ? item.name === "Bespoke"
                          ? "text-amber-400 bg-amber-400/10 font-semibold drop-shadow-lg drop-shadow-amber-400/50"
                          : isTransparent
                          ? "text-amber-400 bg-amber-400/10 font-semibold drop-shadow-lg drop-shadow-amber-400/50"
                          : "text-primary bg-primary/10 font-semibold drop-shadow-lg drop-shadow-primary/50"
                        : item.name === "Bespoke"
                        ? "text-amber-400 hover:text-amber-200 hover:bg-amber-400/20 hover:drop-shadow-lg hover:drop-shadow-amber-400/70 hover:scale-105"
                        : isTransparent
                        ? item.name === "Home"
                          ? "text-white hover:text-amber-300 hover:bg-amber-400/20 hover:drop-shadow-lg hover:drop-shadow-amber-300/50 hover:scale-105"
                          : item.name === "About"
                          ? "text-white hover:text-amber-300 hover:bg-amber-400/20 hover:drop-shadow-lg hover:drop-shadow-amber-300/50 hover:scale-105"
                          : item.name === "Contact"
                          ? "text-white hover:text-amber-300 hover:bg-amber-400/20 hover:drop-shadow-lg hover:drop-shadow-amber-300/50 hover:scale-105"
                          : "text-white hover:text-amber-300 hover:bg-amber-400/20 hover:drop-shadow-lg hover:drop-shadow-amber-300/50 hover:scale-105"
                        : "text-foreground hover:text-primary hover:bg-muted/50 hover:drop-shadow-lg hover:drop-shadow-primary/50 hover:scale-105"
                    }`}
                    role="menuitem"
                    aria-label={`Navigate to ${item.name}`}
                  >
                    {item.name}
                  </button>
                </li>
              );
            })}
            <li role="none" className="pt-2">
              <Button
                onClick={() => {
                  if (onOpenForm) {
                    onOpenForm();
                    setIsOpen(false);
                  } else {
                    setIsFormOpen(true);
                    setIsOpen(false);
                  }
                }}
                className={`w-full transition-all duration-300 ${
                  isTransparent
                    ? "bg-white/20 hover:bg-white/30 text-white border border-white/30 backdrop-blur-sm"
                    : "bg-foreground text-background hover:bg-foreground/70"
                }`}
                aria-label="Join waitlist for PhotoZoot"
              >
                Join Waitlist
              </Button>
            </li>
          </ul>
        </div>
      </div>

      {/* Lead Form Modal */}
      <LeadForm
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        title="Get Started with PhotoZoot"
        buttonText="Submit & Join Waitlist"
      />
    </>
  );
}
